import dayjs from 'dayjs';
import _ from 'lodash';
import 'dayjs/locale/fr';

import { Icon } from '@mairistem/composants';

import type { CycleAccueilType } from '@/types';
import { formatMonetaire } from '@/utils';
import TileBase from './TileBase';
import TileHeader from './TileHeader';

dayjs.locale('fr');

const SEUIL_URGENCE_JOURS = 7;

type CycleEtatBadgePropsType = {
  etat: CycleAccueilType['etat'];
};

const CycleEtatBadge = ({ etat }: CycleEtatBadgePropsType) => {
  const getEtatClass = () => {
    switch (etat) {
      case 'Encaissement en cours':
        return 'encaissement-en-cours';
      case 'Traitement des impayés':
        return 'traitement-impayes';
      case 'Encaissement terminé':
        return 'encaissement-termine';
      default:
        return '';
    }
  };

  return <span className={`cycle-etat-badge ${getEtatClass()}`}>{etat}</span>;
};

type DateLimiteContentPropsType = {
  dateLimite: string;
  dateLimiteProlongee?: string;
  etat: CycleAccueilType['etat'];
};

const DateLimiteContent = ({
  dateLimite,
  dateLimiteProlongee,
  etat,
}: DateLimiteContentPropsType) => {
  const dateAffichee = dateLimiteProlongee || dateLimite;
  const joursRestants = dayjs(dateAffichee).diff(dayjs().startOf('day'), 'day');

  // Classe de couleur selon l'état du cycle
  const getColorClass = () => {
    // Rouge si traitement des impayés
    if (etat === 'Traitement des impayés') {
      return 'impayes';
    }
    // Grisé si encaissement terminé
    if (etat === 'Encaissement terminé') {
      return 'termine';
    }
    // Orange si < 7 jours (uniquement pour encaissement en cours)
    if (joursRestants >= 0 && joursRestants < SEUIL_URGENCE_JOURS) {
      return 'urgent';
    }
    return '';
  };

  const colorClass = getColorClass();

  // Si date prolongée, afficher avec title HTML
  if (dateLimiteProlongee) {
    const titleText = `Date limite non prolongée : ${dayjs(dateLimite).format('DD/MM/YYYY')}\nDate limite prolongée : ${dayjs(dateLimiteProlongee).format('DD/MM/YYYY')}`;

    return (
      <span className={`cycle-date-limite ${colorClass}`} title={titleText}>
        <Icon name="clock outline" />
        {dayjs(dateLimiteProlongee).format('DD/MM/YYYY')}
      </span>
    );
  }

  return (
    <span className={`cycle-date-limite ${colorClass}`}>
      <Icon name="clock outline" />
      {dayjs(dateLimite).format('DD/MM/YYYY')}
    </span>
  );
};

type CycleRowPropsType = {
  cycle: CycleAccueilType;
};

const CycleRow = ({ cycle }: CycleRowPropsType) => {
  const {
    titre,
    nombreFactures,
    nombreFacturesPrelevees,
    montant,
    montantEncaisse,
    dateLimite,
    dateLimiteProlongee,
    etat,
    tauxRecouvrement,
  } = cycle;

  const facturesText =
    nombreFacturesPrelevees > 0
      ? `(${nombreFactures} facture${nombreFactures > 1 ? 's' : ''} dont ${nombreFacturesPrelevees} prélevée${nombreFacturesPrelevees > 1 ? 's' : ''})`
      : `(${nombreFactures} facture${nombreFactures > 1 ? 's' : ''})`;

  const montantText = `${formatMonetaire(montantEncaisse, 2, true)} / ${formatMonetaire(montant, 2, true)}`;

  return (
    <div className="cycle-row">
      <div className="cycle-row-header">
        <span className="cycle-row-titre">
          {titre}
          <span className="cycle-row-factures">{facturesText}</span>
        </span>
        <span className="cycle-row-montant">{montantText}</span>
      </div>

      <div className="cycle-row-info">
        <CycleEtatBadge etat={etat} />
        <DateLimiteContent
          dateLimite={dateLimite}
          dateLimiteProlongee={dateLimiteProlongee}
          etat={etat}
        />
        <div className="cycle-row-progress">
          <span className="cycle-row-progress-label">Taux de recouvrement</span>
          <div
            className={`cycle-row-progress-bar${etat === 'Traitement des impayés' ? ' warning' : ''}`}
          >
            <div
              className="cycle-row-progress-bar-fill"
              style={{ width: `${Math.min(100, Math.max(tauxRecouvrement, 5))}%` }}
            >
              <span className="cycle-row-progress-bar-percent">{`${tauxRecouvrement.toFixed(0)} %`}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

type TileCyclesPropsType = {
  cycles: CycleAccueilType[];
  loading?: boolean;
};

const TileCycles = ({ cycles, loading }: TileCyclesPropsType) => (
  <TileBase className="tile-cycles" loading={loading}>
    <TileHeader title="Cycles de facturation" />

    {_.isEmpty(cycles) ? (
      <div className="tile-cycles-empty">Aucun cycle à encaisser</div>
    ) : (
      <div className="tile-cycles-list">
        {_.map(cycles, (cycle) => (
          <CycleRow key={cycle.identifiant} cycle={cycle} />
        ))}
      </div>
    )}
  </TileBase>
);

export default TileCycles;
